import React, { useEffect, useState } from "react";
import { PageHero, SectionTitle } from "@/components/common/Bits";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { Linkedin, Mail, FileText, Download } from "lucide-react";

/* Hakkımızda */
export function AboutPage() {
  const { t } = useLanguage();
  return (
    <div data-testid="page-about">
      <PageHero overline={t.ov_corporate} title={t.nav_about_us} image="https://customer-assets-39nsmqrw.emergentagent.net/job_useko-web/artifacts/337nub87_4-hakkimizda.png"/>
      <section className="py-20 useko-container max-w-4xl">
        <div className="prose-useko">
          <p>USEKO; ekonomik kalkınma, ticaret, yatırım, eğitim, kültürel diplomasi ve uluslararası organizasyonlar alanında faaliyet gösteren, çok paydaşlı iş birliği modellerini destekleyen uluslararası vizyona sahip bir sivil toplum kuruluşudur. Gerçekleştirdiği çalışmalarla ülkeler, kurumlar ve toplumlar arasında kalıcı değer üretmeyi hedeflemektedir.</p>
        </div>
      </section>
    </div>
  );
}

/* Misyon Vizyon */
export function MissionPage() {
  const { t } = useLanguage();
  return (
    <div data-testid="page-mission">
      <PageHero overline={t.ov_corporate} title={t.nav_mission} image="https://images.unsplash.com/photo-1451976426598-a7593bd6d0b2?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"/>
      <section className="py-20 useko-container">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="p-10 rounded-3xl bg-[#EAF7F0] border border-[#1F8A5B]/10">
            <div className="overline mb-3">Misyon</div>
            <h2 className="font-heading text-3xl font-bold text-[#102A43] mb-4">Misyon</h2>
            <p className="text-[#4B5563] leading-relaxed">Ulusal ve uluslararası düzeyde ekonomik kalkınmayı destekleyen, ticari ve stratejik iş birliklerini güçlendiren, bilgi ve deneyim paylaşımını teşvik eden, sürdürülebilir ve kapsayıcı ortaklık modelleri geliştirmek.</p>
          </div>
          <div className="p-10 rounded-3xl bg-[#102A43] text-white">
            <div className="overline mb-3" style={{color:"#EAF7F0"}}>Vizyon</div>
            <h2 className="font-heading text-3xl font-bold mb-4">Vizyon</h2>
            <p className="text-white/80 leading-relaxed">Türkiye'nin ekonomik, ticari ve diplomatik kapasitesinin uluslararası ölçekte daha güçlü temsil edilmesine katkı sağlayan, güvenilir ve saygın bir küresel iş birliği platformu olmak.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

/* Temel İlkelerimiz */
export function PrinciplesPage() {
  const { t } = useLanguage();
  return (
    <div data-testid="page-principles">
      <PageHero overline={t.ov_corporate} title={t.nav_principles}/>
      <section className="py-20 useko-container max-w-4xl">
        <div className="prose-useko">
          <p>USEKO; sürdürülebilirlik, güven, şeffaflık, etik yönetim, katılımcılık, hesap verebilirlik, kapsayıcılık, uluslararası iş birliği, toplumsal fayda ve ortak değer üretimi ilkeleri doğrultusunda faaliyet göstermektedir.</p>
        </div>
      </section>
    </div>
  );
}

/* Organizasyon Yapısı */
export function OrganizationPage() {
  return (
    <div data-testid="page-organization">
      <PageHero overline="Kurumsal" title="Organizasyon Yapısı"/>
      <section className="py-20 bg-white">
        <div className="useko-container">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Sektörel */}
            <div className="p-8 md:p-10 bg-[#FBFAF6] border border-[#E5E7EB] rounded-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#D4A94F] flex items-center justify-center text-[#0B1837] font-bold text-lg">S</div>
                <h3 className="font-heading text-2xl font-bold text-[#0B1837]">Sektörel Başkanlıklar</h3>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#374151]">
                {["İnşaat ve Altyapı","Enerji ve Madencilik","Savunma ve Havacılık","Teknoloji ve Dijital Dönüşüm","Tarım, Gıda ve Su","Sağlık","Turizm","Tekstil, Moda ve Mobilya Dekorasyon","Finans ve Yatırım","Lojistik ve Ulaştırma"].map((s, i) => (
                  <li key={s} className="flex items-start gap-3">
                    <span className="text-[#B8862F] font-semibold text-xs mt-0.5 min-w-[20px]">{String(i+1).padStart(2,'0')}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Fonksiyonel */}
            <div className="p-8 md:p-10 bg-[#FBFAF6] border border-[#E5E7EB] rounded-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#0B1837] flex items-center justify-center text-[#EDE4CE] font-bold text-lg">F</div>
                <h3 className="font-heading text-2xl font-bold text-[#0B1837]">Fonksiyonel Başkanlıklar</h3>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#374151]">
                {["Hukuk ve Mevzuat","Mali İşler","İnsan Kaynakları","Kurumsal İletişim","Uluslararası İlişkiler ve Lobicilik","AR-GE ve İnovasyon","Dijital Dönüşüm","Strateji ve Planlama","Proje Geliştirme","Proje ve Organizasyon","Kaynak Geliştirme ve Fon Yönetimi","Sosyal Yardımlaşma ve Dayanışma"].map((s, i) => (
                  <li key={s} className="flex items-start gap-3">
                    <span className="text-[#B8862F] font-semibold text-xs mt-0.5 min-w-[20px]">{String(i+1).padStart(2,'0')}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* Tüzük ve Belgeler */
export function DocumentsPage() {
  const { t } = useLanguage();
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    api.get("/documents").then(r => setDocs(r.data)).catch(()=>{});
  }, []);
  return (
    <div data-testid="page-documents">
      <PageHero overline={t.ov_corporate} title={t.nav_documents} description={t.documents_desc}/>
      <section className="py-20 useko-container max-w-3xl">
        {docs.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#E5E7EB] rounded-2xl">
            <FileText className="w-10 h-10 mx-auto text-[#4B5563] opacity-40"/>
            <p className="mt-4 text-[#4B5563]">{t.documents_empty}</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {docs.map(d => (
              <li key={d.id}>
                <a href={d.file_url} target="_blank" rel="noreferrer" className="flex items-center justify-between p-5 useko-card group" data-testid={`doc-${d.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-[#EAF7F0] flex items-center justify-center"><FileText className="w-5 h-5 text-[#1F8A5B]"/></div>
                    <div>
                      <div className="font-medium text-[#102A43]">{d.title}</div>
                      <div className="text-xs text-[#4B5563] uppercase tracking-widest mt-0.5">{d.category}</div>
                    </div>
                  </div>
                  <Download className="w-5 h-5 text-[#4B5563] group-hover:text-[#1F8A5B]"/>
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/* Başkanın Mesajı */
export function PresidentPage() {
  const { t } = useLanguage();
  return (
    <div data-testid="page-president">
      <PageHero overline="Kurumsal" title={t.president_title}/>
      <section className="py-20 useko-container max-w-4xl">
        <div className="grid md:grid-cols-3 gap-10 items-start">
          <div className="md:col-span-1">
            <div className="aspect-square rounded-2xl bg-gradient-to-br from-[#EAF7F0] to-[#1F8A5B]/20 flex items-center justify-center">
              <div className="text-6xl font-heading font-bold text-[#0F5132]">OU</div>
            </div>
            <div className="mt-4">
              <div className="font-heading text-xl font-bold text-[#102A43]">Oktay Ulusal</div>
              <div className="text-sm text-[#4B5563]">USEKO Başkanı</div>
            </div>
          </div>
          <div className="md:col-span-2 prose-useko">
            <p className="text-lg">{t.president_body}</p>
            <p>Uluslararası vizyonumuz doğrultusunda; şeffaflık, hesap verebilirlik ve etik yönetim ilkelerini merkeze koyarak, ölçülebilir etki üretmeye devam edeceğiz.</p>
            <p>Bu yolculuğa katkı sağlamak isteyen tüm bireyleri, kurumları ve iş dünyası temsilcilerini USEKO ailesine katılmaya davet ediyoruz.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

/* Yönetim Kurulu */
export function BoardPage() {
  const { t } = useLanguage();
  const [members, setMembers] = useState([]);
  useEffect(() => { api.get("/board-members").then(r => setMembers(r.data)).catch(()=>{}); }, []);
  const initials = (name) => name.split(" ").map(p => p[0]).slice(0,2).join("").toUpperCase();
  return (
    <div data-testid="page-board">
      <PageHero overline={t.ov_corporate} title={t.nav_board} description={t.board_desc}/>
      <section className="py-20 useko-container">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {members.map((m) => (
            <div key={m.id} className="useko-card p-6" data-testid={`board-member-${m.id}`}>
              {m.photo ? (
                <img src={m.photo} alt={m.name} className="w-20 h-20 rounded-full object-cover mb-4"/>
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#EAF7F0] to-[#1F8A5B]/25 flex items-center justify-center mb-4">
                  <span className="font-heading font-bold text-[#0F5132] text-lg">{initials(m.name)}</span>
                </div>
              )}
              <h3 className="font-heading text-lg font-bold text-[#102A43]">{m.name}</h3>
              <div className="text-sm text-[#1F8A5B] font-medium mt-1">{m.role_title}</div>
              {m.professional_title && <div className="text-xs text-[#4B5563] uppercase tracking-widest mt-1">{m.professional_title}</div>}
              {m.bio && <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">{m.bio}</p>}
              <div className="mt-4 flex gap-2">
                {m.linkedin_url && <a href={m.linkedin_url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg bg-[#F7F9FA] hover:bg-[#EAF7F0] flex items-center justify-center"><Linkedin className="w-4 h-4 text-[#1F8A5B]"/></a>}
                {m.email && <a href={`mailto:${m.email}`} className="w-8 h-8 rounded-lg bg-[#F7F9FA] hover:bg-[#EAF7F0] flex items-center justify-center"><Mail className="w-4 h-4 text-[#1F8A5B]"/></a>}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
